/**
 * Die Gliederung des Gewerks Sprinkler — an EINER Stelle.
 * Muster N1 (Kopfkommentar `seiten/heizlastkuehllast/abschnitte.ts`),
 * umgesetzt in Paket N2.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ZEHN REITER, GEMESSEN AN `Sprinkler.tsx` (NICHT AN DEN 29 „REITER-SPUREN")
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Plan Abschnitt 3.2 nennt „29 Reiter-Spuren" — ein Grep-Treffer über die
 * gesamte Datei (u. a. `Anlagenknoten`, `Wirkflaechenknoten`, `Wegknoten`,
 * `Postenknoten`, `Stationsknoten`, `Steigknoten` — das sind Baumknoten
 * INNERHALB der Aufschlüsselung „Wege und Teilstrecken", keine eigenen
 * Reiter). Gemessen an der echten Knopfzeile (`abschnitt`-Zustand, jetzt
 * aufgelöst) sind es genau ZEHN: Bestand, Ebenen und Räume, Netze,
 * Sprinkler, Berechnung, Wege und Teilstrecken, Steigleitung, Geräte,
 * Anlagentechnik, Konfiguration.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DIE VIER STANDARD-ZWISCHENZEILEN, GEMESSEN VERTEILT
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Planung: Netze (`sprinkler_netze`, Plan: „planung: Stationen, Netze" —
 * „Stationen" ist keine eigene Seite, sondern ein Knoten INNERHALB von
 * „Wege und Teilstrecken", s. o.).
 *
 * Berechnungen — hier wird sichtbar, was der Auftrag verlangt:
 *   - Berechnung: die Hydraulik/Teilstreckenrechnung des Sprinklernetzes
 *     (`sprinkler_teilstrecken`, `sprinkler_berechnung`).
 *   - Wege und Teilstrecken: die Aufschlüsselung Anlage → Station →
 *     Wirkfläche → Weg → Teilstrecke → Posten (`sprinkler_wirkflaechen`) —
 *     das sind die WIRKFLÄCHEN aus dem Auftrag.
 *   - Steigleitung: dieselbe Berechnungskomponente, Bemessung nach
 *     3 × 200 l/min statt Wirkfläche.
 *   - Anlagentechnik: Feuerlöschpumpen mit der n−1-Prüfung
 *     (`sprinkler_pumpen`, Förderstrom/Förderhöhe je Pumpe) und die
 *     Wandhydranten mit berechnetem Fließdruck/„maßgebend" — das sind die
 *     PUMPEN aus dem Auftrag.
 *
 * Daten: Bestand (Kennzahlenübersicht — Index, fällt in `alleEintraege`
 * automatisch heraus), Sprinkler (die Kopfliste, `sprinkler_koepfe`),
 * Geräte (Produktdaten/Kennfelder), Konfiguration (Regelwerk, Verfahren,
 * Brandrisiko/Gefahrenklasse, Steigleitungs-Grundwerte —
 * `sprinkler_konfiguration`, deckt „Kennfelder, Typregeln,
 * Gefahrenklassen" aus dem Plan ab).
 *
 * Prüfung: Ebenen und Räume — sie ist ausdrücklich eine „Prüfrechnung und
 * kein Nachweis" (Kopfkommentar der Komponente `Raumbaum`): Mindestanzahl
 * Köpfe je Raum aus der Fläche, keine eigene Bemessung.
 *
 * Nicht übernommen aus dem Plan: „Modellanweisungen"
 * (`sprinkler_modellanweisung`, Plan: prüfung) — dafür gibt es keine eigene
 * Seite; Navigation verweist nur auf real Vorhandenes.
 */

export interface SprinklerEintrag {
  /** Der Pfad UNTER /projekte/:id/sprinkler/ (bzw. 'sprinkler' selbst fuer den Index). */
  pfad: string;
  name: string;
  /** Wozu die Seite da ist. Erscheint als Titel am Baumeintrag. */
  hinweis: string;
}

export interface SprinklerAbschnitt {
  name: string;
  eintraege: SprinklerEintrag[];
}

export const SPRINKLER_ABSCHNITTE: SprinklerAbschnitt[] = [
  {
    name: 'Planung',
    eintraege: [
      {
        pfad: 'sprinkler/netze',
        name: 'Netze',
        hinweis: 'Die drei getrennt bemessenen Netze: Sprinklernetz nass, '
          + 'Steigleitung nass (Wandhydranten), Steigleitung trocken',
      },
    ],
  },
  {
    name: 'Berechnungen',
    eintraege: [
      {
        pfad: 'sprinkler/berechnung',
        name: 'Berechnung',
        hinweis: 'Die Hydraulik-/Teilstreckenrechnung des Sprinklernetzes',
      },
      {
        pfad: 'sprinkler/nachweis',
        name: 'Wege und Teilstrecken',
        hinweis: 'Die Aufschlüsselung Anlage → Station → Wirkfläche → Weg '
          + '→ Teilstrecke → Posten',
      },
      {
        pfad: 'sprinkler/steigleitung',
        name: 'Steigleitung',
        hinweis: 'Bemessung der nassen Steigleitung nach 3 × 200 l/min',
      },
      {
        pfad: 'sprinkler/technik',
        name: 'Anlagentechnik',
        hinweis: 'Feuerlöschpumpen mit n−1-Prüfung und die maßgebenden '
          + 'Wandhydranten nach Fließdruck',
      },
    ],
  },
  {
    name: 'Daten',
    eintraege: [
      {
        // Index-Route — pfad ist absichtlich der Fachbereichspfad selbst,
        // siehe Kopfkommentar Punkt 3 in heizlastkuehllast/abschnitte.ts.
        pfad: 'sprinkler',
        name: 'Bestand',
        hinweis: 'Kennzahlen: Köpfe, Rohre, Verbindungen, Netze',
      },
      {
        pfad: 'sprinkler/koepfe',
        name: 'Sprinkler',
        hinweis: 'Die Kopfliste mit Lage, Typ und Netzzuordnung',
      },
      {
        pfad: 'sprinkler/geraete',
        name: 'Geräte',
        hinweis: 'Fabrikate und Kennwerte — dieselbe Detailseite wie bei '
          + 'Heizung, Lüftung und Sanitär',
      },
      {
        pfad: 'sprinkler/konfiguration',
        name: 'Konfiguration',
        hinweis: 'Regelwerk, Berechnungsverfahren, Brandrisiko/'
          + 'Gefahrenklasse und die Grundwerte der Steigleitung',
      },
    ],
  },
  {
    name: 'Prüfung',
    eintraege: [
      {
        pfad: 'sprinkler/raeume',
        name: 'Ebenen und Räume',
        hinweis: 'Prüfrechnung je Raum: Mindestanzahl Köpfe aus der '
          + 'Fläche — kein Nachweis, findet nur offensichtlich zu wenige',
      },
    ],
  },
];
